import type { Components } from "react-markdown";
import ReactMarkdown from "react-markdown";
import remarkBreaks from "remark-breaks";
import Link from "~/components/Link";

import Box from "@mui/material/Box";
import Divider from "@mui/material/Divider";
import type { Theme } from "@mui/material/styles";
import type { SxProps } from "@mui/system";

export interface MarkdownProps {
  children: string;
  sx?: SxProps<Theme>;
  disableEmphasizeHeadings?: boolean;
  disableParagraphMargin?: boolean;
  components?: Partial<Components>;
}

const markdownComponents: Components = {
  a: ({ href, children, ...props }) => {
    // Check if it's an internal link (starts with / or doesn't have a protocol)
    const isInternal =
      href &&
      (href.startsWith("/") ||
        (!href.startsWith("http://") && !href.startsWith("https://")));

    if (isInternal) {
      // Use the custom Link component for internal links
      return (
        <Link href={href || "#"} effect="underline">
          {children}
        </Link>
      );
    }

    // Use regular anchor tag for external links with target="_blank"
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" {...props}>
        {children}
      </a>
    );
  },
  hr: () => <Divider component="hr" />,
};

export default function Markdown({
  children,
  sx,
  disableEmphasizeHeadings,
  disableParagraphMargin,
  components,
}: MarkdownProps) {
  return (
    <Box
      sx={{
        lineHeight: 1,
        "& h1, & h2, & h3, & h4, & h5, & h6": disableEmphasizeHeadings
          ? {}
          : {
            fontFamily: "var(--font-headline)",
            fontWeight: 600,
            color: "primary.main",
            mt: 4,
            mb: 2,
          },
        "& h1": { fontSize: { xs: "1.75em", md: "2.25em" } },
        "& h2": { fontSize: { xs: "1.5em", md: "2em" } },
        "& h3": { fontSize: { xs: "1.25em", md: "1.5em" } },
        "& p": {
          fontFamily: "var(--font-body)",
          fontSize: "1em",
          lineHeight: 1.8,
          color: "text.primary",
          ...(disableParagraphMargin ? { mb: 0, mt: 0 } : { mb: 2 }),
        },
        "& ul": {
          pl: 3,
          mb: disableParagraphMargin ? 0 : 2,
          listStyleType: "disc",
        },
        "& ol": {
          pl: 3,
          mb: disableParagraphMargin ? 0 : 2,
          listStyleType: "decimal",
        },
        // Nested list styles
        "& ul ul": {
          listStyleType: "circle",
          mb: disableParagraphMargin ? 0 : 2,
          pl: 3,
        },
        "& ul ul ul": {
          listStyleType: "square",
          mb: disableParagraphMargin ? 0 : 2,
          pl: 3,
        },
        "& ol ol": {
          listStyleType: "lower-alpha",
          mb: disableParagraphMargin ? 0 : 2,
          pl: 3,
        },
        "& ol ol ol": {
          listStyleType: "lower-roman",
          mb: disableParagraphMargin ? 0 : 2,
          pl: 3,
        },
        // Mixed nested list styles
        "& ol ul": {
          listStyleType: "circle",
          mb: disableParagraphMargin ? 0 : 2,
          pl: 3,
        },
        "& ul ol": {
          listStyleType: "lower-alpha",
          mb: disableParagraphMargin ? 0 : 2,
          pl: 3,
        },
        "& li": {
          fontFamily: "var(--font-body)",
          fontSize: "1em",
          lineHeight: 1.8,
          color: "text.primary",
          mb: disableParagraphMargin ? 0 : 1,
        },
        "& blockquote": {
          borderLeftStyle: "solid",
          borderLeftWidth: 4,
          borderColor: "primary.main",
          pl: 3,
          ml: 0,
          py: 1,
          my: 3,
          fontStyle: "italic",
          color: "text.secondary",
          ":hover": {
            borderLeftWidth: 8,
            ml: "-4px",
          },
        },
        "& a": {
          color: "primary.main",
          textDecoration: "none",
          "&:hover": {
            textDecoration: "underline",
          },
        },
        "& strong": {
          fontWeight: 600,
        },
        "& code": {
          padding: "2px 6px",
          borderRadius: 1,
          fontFamily: "monospace",
        },
        "& pre": {
          maxWidth: "100%",
          overflowX: "auto",
          "& code": {
            padding: 0,
            borderRadius: 0,
          },
        },
        ...sx,
      }}
    >
      <ReactMarkdown
        remarkPlugins={[remarkBreaks]}
        components={{ ...markdownComponents, ...components }}
      >
        {children}
      </ReactMarkdown>
    </Box>
  );
}
